import * as React from "react"
import { cn } from "../lib/utils"
import { Button } from "./button"
import { Input } from "./input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table"
import { Badge } from "./badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select"
import { Search } from "lucide-react"

export interface Provider {
  id: string
  profile_id: string
  first_name: string
  last_name: string
  email: string
  phone?: string
  specialty?: string
  license_number?: string
  created_at: string
  is_active?: boolean
  // Aggregated data
  assigned_patients_count?: number
  upcoming_visits_count?: number
  treatment_types?: string[]
}

export interface ProviderTableProps {
  providers: Provider[]
  loading?: boolean
  searchPlaceholder?: string
  showSearch?: boolean
  onProviderClick?: (provider: Provider) => void
  emptyStateText?: string
  className?: string
  title?: string
  subtitle?: string
  showResultsCount?: boolean
}

export function ProviderTable({
  providers,
  loading = false,
  searchPlaceholder = "Search providers...",
  showSearch = true,
  onProviderClick,
  emptyStateText = "No providers found.",
  className,
  title,
  subtitle,
  showResultsCount = true,
}: ProviderTableProps) {
  const [searchTerm, setSearchTerm] = React.useState("")
  const [pageSize, setPageSize] = React.useState(8)
  const [currentPage, setCurrentPage] = React.useState(0)
  const [tableWidth, setTableWidth] = React.useState<number>(600)
  const mainContainerRef = React.useRef<HTMLDivElement>(null)

  const filteredProviders = React.useMemo(() => {
    if (!searchTerm) return providers

    const term = searchTerm.toLowerCase()
    return providers.filter(
      (provider) =>
        provider.first_name?.toLowerCase().includes(term) ||
        provider.last_name?.toLowerCase().includes(term) ||
        provider.email?.toLowerCase().includes(term) ||
        provider.specialty?.toLowerCase().includes(term)
    )
  }, [providers, searchTerm])

  // Pagination logic
  const totalPages = Math.ceil(filteredProviders.length / pageSize)
  const paginatedProviders = React.useMemo(() => {
    const startIndex = currentPage * pageSize
    return filteredProviders.slice(startIndex, startIndex + pageSize)
  }, [filteredProviders, currentPage, pageSize])

  // Reset to first page when search changes
  React.useEffect(() => {
    setCurrentPage(0)
  }, [searchTerm])

  // Calculate available width
  React.useEffect(() => {
    let rafId: number

    const calculateTableWidth = () => {
      if (mainContainerRef.current) {
        const containerWidth = mainContainerRef.current.offsetWidth
        const minWidth = 800
        const calculatedWidth = Math.max(minWidth, containerWidth)
        setTableWidth(calculatedWidth)
      }
    }

    const debouncedCalculate = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(calculateTableWidth)
    }

    calculateTableWidth()
    setTimeout(calculateTableWidth, 0)
    setTimeout(calculateTableWidth, 100)

    const resizeObserver = new ResizeObserver(debouncedCalculate)

    if (mainContainerRef.current) {
      resizeObserver.observe(mainContainerRef.current)
    }

    window.addEventListener('resize', debouncedCalculate)
    window.addEventListener('orientationchange', debouncedCalculate)
    document.addEventListener('fullscreenchange', debouncedCalculate)

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      resizeObserver.disconnect()
      window.removeEventListener('resize', debouncedCalculate)
      window.removeEventListener('orientationchange', debouncedCalculate)
      document.removeEventListener('fullscreenchange', debouncedCalculate)
    }
  }, [])

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  const formatName = (first_name: string, last_name: string) => {
    return `${first_name} ${last_name}`.trim() || "Unknown"
  }

  const abbreviateId = (id: string) => {
    return id.slice(0, 8).toUpperCase()
  }

  if (loading) {
    return (
      <div className={cn("w-full p-6", className)}>
        {(title || subtitle) && (
          <div className="space-y-2 mb-6">
            {title && <div className="h-9 bg-muted animate-pulse rounded-md w-64" />}
            {subtitle && <div className="h-5 bg-muted animate-pulse rounded-md w-96" />}
          </div>
        )}

        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-4">
            {showSearch && (
              <div className="max-w-sm h-10 bg-muted animate-pulse rounded-md" />
            )}
          </div>
        </div>
        <div className="border rounded-lg overflow-hidden">
          <div className="h-12 bg-muted animate-pulse" />
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-16 border-t bg-background animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div ref={mainContainerRef} className={cn("w-full overflow-hidden", className)}>
      {/* Title and Subtitle */}
      {(title || subtitle) && (
        <div className="space-y-2 mb-6 px-6">
          {title && <h1 className="text-3xl font-bold">{title}</h1>}
          {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
        </div>
      )}

      {/* Header with search */}
      <div className="flex items-center justify-between py-4 px-6">
        <div className="flex items-center gap-4">
          {showSearch && (
            <Input
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          )}
        </div>
      </div>

      {/* Results count */}
      {showResultsCount && (
        <div className="text-sm text-muted-foreground pb-4 px-6">
          Showing {filteredProviders.length} of {providers.length} providers
        </div>
      )}

      {/* Table */}
      <div className="w-full overflow-x-auto border-l border-r">
        <div
          className="overflow-x-auto"
          style={{ width: `${tableWidth}px`, minWidth: '100%' }}
        >
        <Table className="min-w-[800px]">
          <TableHeader>
            <TableRow>
              <TableHead>Provider</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Specialty</TableHead>
              <TableHead>Patients Assigned</TableHead>
              <TableHead>Upcoming Visits</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProviders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <div className="text-muted-foreground">
                    {searchTerm ? "No providers match your search." : emptyStateText}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              paginatedProviders.map((provider) => (
                <TableRow
                  key={provider.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onProviderClick?.(provider)}
                >
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium text-primary hover:underline">
                        {formatName(provider.first_name, provider.last_name)}
                      </span>
                      <span className="text-sm text-muted-foreground">{provider.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm text-muted-foreground">
                      {abbreviateId(provider.id)}
                    </span>
                  </TableCell>
                  <TableCell>
                    {provider.specialty ? (
                      <Badge variant="outline" className="capitalize">
                        {provider.specialty.replace('_', ' ')}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">
                      {provider.assigned_patients_count ?? 0}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">
                      {provider.upcoming_visits_count ?? 0}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={provider.is_active !== false ? 'default' : 'secondary'}>
                      {provider.is_active !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between space-x-2 py-4 px-6">
        <div className="text-sm text-muted-foreground">
          {filteredProviders.length > 0
            ? `Showing ${currentPage * pageSize + 1} to ${Math.min((currentPage + 1) * pageSize, filteredProviders.length)} of ${filteredProviders.length} providers`
            : "No providers to display"
          }
        </div>
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${pageSize}`}
              onValueChange={(value) => {
                setPageSize(Number(value))
                setCurrentPage(0)
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="top">
                {[4, 8, 10, 20].map((size) => (
                  <SelectItem key={size} value={`${size}`}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-[100px] items-center justify-center text-sm font-medium">
            Page {currentPage + 1} of {totalPages || 1}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
            >
              <span className="sr-only">Go to first page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 0}
            >
              <span className="sr-only">Go to previous page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              <span className="sr-only">Go to next page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
            >
              <span className="sr-only">Go to last page</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
